/* eslint-disable */


import React, { useEffect, useMemo, useState } from "react";
import { AccountLink } from "../components/resellers/AccountLink";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { sortByName } from "../lib/utils";
import { fmtDate, fmtDateTime, fmtNum } from "../lib/format";
import { FileText, Plus, Trash2, Search, X, Eye, ClipboardCheck, AlertTriangle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Card, CardContent } from "../components/ui/card";
import { useConfirm } from "../components/ConfirmDialog";
import { toast } from "sonner";

const STATUS_META = {
  BROUILLON: { label: "Brouillon", className: "bg-[#F0F0EB] text-[#575A57] border-[#E5E5E0]" },
  ENVOYE: { label: "Envoyé", className: "bg-[#FBF4E9] text-[#8C6B2F] border-[#E8D3A8]" },
  LIVRE: { label: "Livré", className: "bg-[#E5EFF8] text-[#2D5A87] border-[#C5D8E8]" },
  SIGNE: { label: "Signé", className: "bg-[#EAF4EE] text-[#1F4E3D] border-[#C5DBCF]" },
  ANNULE: { label: "Annulé", className: "bg-[#FCEEEA] text-[#C05C47] border-[#E8C1B4]" },
};

export default function DeliveryNotes() {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [notes, setNotes] = useState([]);
  const [products, setProducts] = useState([]);
  const [distributors, setDistributors] = useState([]);
  const [lots, setLots] = useState([]);
  const [open, setOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterDist, setFilterDist] = useState("all");
  const [search, setSearch] = useState("");
  // Phase 96d — Multi-sélection pour suppression forcée (admin only, cf. AuthContext role check)
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectAll = (visibleBls) => {
    setSelectedIds((prev) => {
      const allSelected = visibleBls.every((bl) => prev.has(bl.id));
      if (allSelected) return new Set();
      return new Set(visibleBls.map((bl) => bl.id));
    });
  };
  const bulkForceDelete = async () => {
    if (selectedIds.size === 0) return;
    // Phase 101fl (Feb 2026) — Alain : « Sur suppression forcée d'un BL signé,
    // propose de créer l'avoir en un clic ». Le dialog demande explicitement
    // si on veut générer les avoirs comptables (par défaut : oui pour les
    // BL signés/livrés facturés).
    const shouldCreateNC = window.confirm(
      `Créer automatiquement un avoir (NC) pour chaque BL supprimé lié à une facture envoyée ?\n\n` +
      `→ OK : NC BROUILLON générée par BL facturé (motif "Annulation commande", restock désactivé)\n` +
      `→ Annuler : suppression seule (attention : facture reste sans compensation comptable)`
    );
    const ok = await confirm({
      title: `Supprimer ${selectedIds.size} BL sélectionnés ?`,
      description: (
        <>
          Cette action est IRRÉVERSIBLE. Les mouvements de stock liés seront conservés (marqués voided) pour la traçabilité HACCP, mais les BL disparaîtront de la liste.
          <br /><br />
          {shouldCreateNC
            ? <span className="text-[#8B3A1F] font-medium">Un avoir sera généré pour chaque BL lié à une facture ENVOYEE+.</span>
            : <span className="text-[#B24A32]">Aucun avoir ne sera créé. Toi de gérer la NC manuellement si besoin.</span>}
        </>
      ),
      confirmText: "Oui, supprimer définitivement",
      variant: "destructive",
    });
    if (!ok) return;
    setBulkDeleting(true);
    try {
      const r = await api.post("/admin/delivery-notes/bulk-force-delete", {
        ids: Array.from(selectedIds),
        create_credit_notes: shouldCreateNC,
      });
      const ncs = r.data.generated_credit_notes || [];
      let msg = `${r.data.deleted} BL supprimés (${r.data.voided_movements} mouvements voided, ${r.data.restored_units} unités restituées)`;
      if (ncs.length > 0) {
        msg += ` · ${ncs.length} avoir(s) BROUILLON généré(s)`;
      }
      toast.success(msg, { duration: 6000 });
      if (ncs.length > 0) {
        // Toast additionnel avec lien direct vers les NCs
        setTimeout(() => {
          toast.info(
            `Avoirs à valider : ${ncs.map((n) => n.credit_note_reference).join(", ")}`,
            {
              duration: 8000,
              action: {
                label: "Ouvrir la liste",
                onClick: () => (window.location.href = "/notes-de-credit"),
              },
            }
          );
        }, 500);
      }
      setSelectedIds(new Set());
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur suppression en masse");
    } finally {
      setBulkDeleting(false);
    }
  };

  const emptyForm = { distributor_id: "", from_distributor_id: "", delivery_date: "", notes: "", lines: [] };
  const [form, setForm] = useState(emptyForm);

  const load = async () => {
    const [n, p, d, l] = await Promise.all([
      api.get("/delivery-notes"),
      api.get("/products"),
      api.get("/distributors"),
      api.get("/lots?active_only=true"),
    ]);
    setNotes(n.data); setProducts(p.data); setDistributors(d.data); setLots(l.data);
  };
  useEffect(() => { load(); }, []);

  const productMap = useMemo(() => Object.fromEntries(products.map((p) => [p.id, p])), [products]);
  const distMap = useMemo(() => Object.fromEntries(distributors.map((d) => [d.id, d])), [distributors]);
  const clientDistributors = useMemo(() => distributors.filter((d) => d.type === "CLIENT"), [distributors]);
  const sourceDistributors = useMemo(() => distributors.filter((d) => d.type === "AUTOMATIQUE"), [distributors]);

  const eligibleLots = useMemo(() => {
    if (!form.from_distributor_id) return [];
    return lots
      .filter((x) => x.distributor_id === form.from_distributor_id && (x.quantity || 0) > 0)
      .sort((a, b) => (a.dlc || "").localeCompare(b.dlc || ""));
  }, [lots, form.from_distributor_id]);

  const addLine = () => setForm({ ...form, lines: [...form.lines, { lot_id: "", product_id: "", quantity: 1, unit_price: "", notes: "" }] });
  const updateLine = (idx, patch) => setForm({ ...form, lines: form.lines.map((ln, i) => i === idx ? { ...ln, ...patch } : ln) });
  const removeLine = (idx) => setForm({ ...form, lines: form.lines.filter((_, i) => i !== idx) });

  const onLotPick = (idx, lotId) => {
    const lot = lots.find((x) => x.id === lotId);
    if (!lot) return updateLine(idx, { lot_id: "" });
    updateLine(idx, { lot_id: lotId, product_id: lot.product_id });
  };

  const save = async () => {
    if (!form.distributor_id) { toast.error("Sélectionnez le client destinataire"); return; }
    if (form.lines.length === 0) { toast.error("Ajoutez au moins une ligne"); return; }
    for (const ln of form.lines) {
      if (!ln.product_id) { toast.error("Toutes les lignes doivent avoir un produit"); return; }
      if (!ln.quantity || Number(ln.quantity) <= 0) { toast.error("Quantité invalide sur une ligne"); return; }
    }
    try {
      const { data } = await api.post("/delivery-notes", {
        distributor_id: form.distributor_id,
        from_distributor_id: form.from_distributor_id || null,
        delivery_date: form.delivery_date || null,
        notes: form.notes,
        lines: form.lines.map((ln) => ({
          lot_id: ln.lot_id || null,
          product_id: ln.product_id,
          quantity: Number(ln.quantity),
          unit_price: ln.unit_price ? Number(ln.unit_price) : null,
          notes: ln.notes || "",
        })),
      });
      toast.success(`Bon de livraison ${data.reference} créé`);
      setOpen(false);
      setForm(emptyForm);
      await load();
      navigate(`/bons-livraison/${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const remove = async (bl) => {
    if (bl.transfer_executed) { toast.error("Impossible : les transferts ont déjà été exécutés"); return; }
    const ok = await confirm({
      title: "Supprimer ce bon de livraison ?",
      description: `Le BL ${bl.reference} sera supprimé. Cette action est irréversible.`,
      confirmText: "Supprimer",
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/delivery-notes/${bl.id}`);
      toast.success("BL supprimé");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const filtered = notes.filter((n) => {
    if (filterStatus !== "all" && n.status !== filterStatus) return false;
    if (filterDist !== "all" && n.distributor_id !== filterDist) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (n.reference || "").toLowerCase().includes(q) ||
           (n.distributor_name || "").toLowerCase().includes(q) ||
           (n.signed_by || "").toLowerCase().includes(q);
  });

  return (
    <div className="space-y-6" data-testid="delivery-notes-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-heading text-[#1A1C1A]">Bons de livraison</h1>
          <p className="text-sm text-[#575A57]">Documents de livraison HACCP pour les clients (réfrigérateurs, restaurants)</p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setForm(emptyForm); }}>
          <DialogTrigger asChild>
            <Button className="bg-[#1F4E3D] hover:bg-[#173B2E]" data-testid="create-bl-btn">
              <Plus className="w-4 h-4 mr-2" /> Nouveau bon de livraison
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading">Nouveau bon de livraison</DialogTitle>
              <DialogDescription>Une référence BL-AAMMJJ-NNN sera générée automatiquement. Le BL est créé en brouillon — vous pourrez exécuter les transferts de stock en un clic depuis sa fiche.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="mb-1.5 block">Client destinataire *</Label>
                  <Select value={form.distributor_id} onValueChange={(v) => setForm({ ...form, distributor_id: v })}>
                    <SelectTrigger data-testid="bl-distributor"><SelectValue placeholder="Sélectionner…" /></SelectTrigger>
                    <SelectContent>
                      {clientDistributors.length === 0 ? (
                        <div className="px-2 py-3 text-xs text-[#575A57]">Aucun client défini. Créez-en un dans Distributeurs.</div>
                      ) : clientDistributors.map((d) => (
                        <SelectItem key={d.id} value={d.id}>{d.name}{d.code ? ` (${d.code})` : ""}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="mb-1.5 block">Dépôt source (pour transfert)</Label>
                  <Select value={form.from_distributor_id || "none"} onValueChange={(v) => setForm({ ...form, from_distributor_id: v === "none" ? "" : v, lines: form.lines.map((ln) => ({ ...ln, lot_id: "" })) })}>
                    <SelectTrigger data-testid="bl-from-depot"><SelectValue placeholder="Optionnel" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Aucun (BL purement administratif)</SelectItem>
                      {sourceDistributors.map((d) => (
                        <SelectItem key={d.id} value={d.id}>{d.name}{d.code ? ` (${d.code})` : ""}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="mb-1.5 block">Date de livraison</Label>
                  <Input type="datetime-local" value={form.delivery_date} onChange={(e) => setForm({ ...form, delivery_date: e.target.value })} data-testid="bl-date" />
                </div>
                <div>
                  <Label className="mb-1.5 block">Notes (optionnel)</Label>
                  <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Conditions, instructions…" data-testid="bl-notes" />
                </div>
              </div>

              {/* Lignes */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Lignes du bon de livraison</Label>
                  <Button variant="outline" size="sm" onClick={addLine} data-testid="bl-add-line"><Plus className="w-3.5 h-3.5 mr-1" /> Ajouter une ligne</Button>
                </div>
                {form.lines.length === 0 ? (
                  <div className="text-xs text-[#A3A6A3] border border-dashed border-[#E5E5E0] rounded p-4 text-center">
                    Cliquez sur « Ajouter une ligne » pour insérer un produit ou un lot.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {form.lines.map((ln, idx) => (
                      <div key={idx} className="grid grid-cols-12 gap-2 items-end p-3 border border-[#E5E5E0] rounded bg-[#FAFAF7]">
                        {form.from_distributor_id && (
                          <div className="col-span-5">
                            <Label className="text-[11px] mb-1 block">Lot source</Label>
                            <Select value={ln.lot_id || "manual"} onValueChange={(v) => v === "manual" ? updateLine(idx, { lot_id: "" }) : onLotPick(idx, v)}>
                              <SelectTrigger className="h-8 text-xs" data-testid={`bl-line-lot-${idx}`}><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="manual">— Saisie libre (sans lot) —</SelectItem>
                                {eligibleLots.map((l) => {
                                  const p = productMap[l.product_id];
                                  return (
                                    <SelectItem key={l.id} value={l.id}>
                                      {l.lot_number} · {p?.name || "?"} · qté {fmtNum(l.quantity)} · DLC {l.dlc || "—"}
                                    </SelectItem>
                                  );
                                })}
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                        <div className={form.from_distributor_id ? "col-span-3" : "col-span-6"}>
                          <Label className="text-[11px] mb-1 block">Produit *</Label>
                          <Select value={ln.product_id} onValueChange={(v) => updateLine(idx, { product_id: v })} disabled={!!ln.lot_id}>
                            <SelectTrigger className="h-8 text-xs" data-testid={`bl-line-product-${idx}`}><SelectValue placeholder="Choisir…" /></SelectTrigger>
                            <SelectContent>
                              {sortByName(products).map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="col-span-2">
                          <Label className="text-[11px] mb-1 block">Quantité *</Label>
                          <Input type="number" step="1" min="1" value={ln.quantity} onChange={(e) => updateLine(idx, { quantity: e.target.value })} className="h-8 text-xs" data-testid={`bl-line-qty-${idx}`} />
                        </div>
                        <div className="col-span-2">
                          <Label className="text-[11px] mb-1 block">PU (CHF)</Label>
                          <Input type="number" step="0.10" value={ln.unit_price} onChange={(e) => updateLine(idx, { unit_price: e.target.value })} className="h-8 text-xs" placeholder="—" data-testid={`bl-line-price-${idx}`} />
                        </div>
                        <div className="col-span-12 md:col-span-12 flex justify-end">
                          <Button variant="ghost" size="sm" onClick={() => removeLine(idx)} className="text-[#C05C47] hover:bg-[#FCEEEA]" data-testid={`bl-line-remove-${idx}`}>
                            <Trash2 className="w-3.5 h-3.5 mr-1" /> Retirer
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
              <Button onClick={save} className="bg-[#1F4E3D] hover:bg-[#173B2E]" data-testid="save-bl-btn">Créer le bon de livraison</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filtres */}
      <div className="flex items-center gap-3 flex-wrap">
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[180px]" data-testid="bl-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les statuts</SelectItem>
            {Object.entries(STATUS_META).map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterDist} onValueChange={setFilterDist}>
          <SelectTrigger className="w-[240px]" data-testid="bl-dist-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les clients</SelectItem>
            {clientDistributors.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 text-[#A3A6A3] absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
          <Input
            type="text"
            placeholder="Rechercher référence, client, signataire…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 pr-8"
            data-testid="bl-search"
          />
          {search && (
            <button type="button" onClick={() => setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#A3A6A3] hover:text-[#575A57]">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Bandeau action bulk (admin) */}
      {isAdmin && selectedIds.size > 0 && (
        <Card className="border-[#E8C1B4] bg-[#FCEEEA]" data-testid="bl-bulk-actions-bar">
          <CardContent className="py-3 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-sm text-[#8C3A2A]">
              <AlertTriangle className="w-4 h-4" />
              <span><strong>{selectedIds.size}</strong> BL sélectionné{selectedIds.size > 1 ? "s" : ""}</span>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setSelectedIds(new Set())} data-testid="bl-clear-selection">
                Désélectionner
              </Button>
              <Button size="sm" variant="destructive" onClick={bulkForceDelete} disabled={bulkDeleting} data-testid="bl-bulk-delete">
                <Trash2 className="w-4 h-4 mr-1" />
                {bulkDeleting ? "Suppression…" : `Supprimer (forcer) ${selectedIds.size}`}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="border-[#E5E5E0]">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                {isAdmin && (
                  <TableHead className="w-8">
                    <input
                      type="checkbox"
                      checked={filtered.length > 0 && filtered.every((bl) => selectedIds.has(bl.id))}
                      onChange={() => toggleSelectAll(filtered)}
                      className="cursor-pointer accent-[#1F4E3D]"
                      data-testid="bl-select-all"
                      title="Tout sélectionner"
                    />
                  </TableHead>
                )}
                <TableHead>Référence</TableHead>
                <TableHead>Date livraison</TableHead>
                <TableHead>Client</TableHead>
                <TableHead>Source</TableHead>
                <TableHead className="text-right">Lignes</TableHead>
                <TableHead className="text-right">Quantité totale</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={isAdmin ? 9 : 8} className="text-center py-12 text-[#A3A6A3]">
                    <FileText className="w-8 h-8 mx-auto mb-2 opacity-30" />
                    Aucun bon de livraison. Cliquez sur « Nouveau bon de livraison » pour commencer.
                  </TableCell>
                </TableRow>
              ) : filtered.map((bl) => {
                const meta = STATUS_META[bl.status] || STATUS_META.BROUILLON;
                const totalQty = (bl.lines || []).reduce((acc, ln) => acc + (ln.quantity || 0), 0);
                return (
                  <TableRow key={bl.id} data-testid={`bl-row-${bl.id}`}>
                    {isAdmin && (
                      <TableCell className="w-8">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(bl.id)}
                          onChange={() => toggleSelect(bl.id)}
                          className="cursor-pointer accent-[#1F4E3D]"
                          data-testid={`bl-select-${bl.id}`}
                        />
                      </TableCell>
                    )}
                    <TableCell className="font-mono text-sm">
                      <Link to={`/bons-livraison/${bl.id}`} className="text-[#1F4E3D] hover:underline">
                        {bl.reference}
                      </Link>
                    </TableCell>
                    <TableCell className="text-xs text-[#575A57]">{fmtDate(bl.delivery_date)}</TableCell>
                    <TableCell>
                      <div><AccountLink resellerId={bl.reseller_id} distributorId={bl.distributor_id} compact testid={`bl-account-${bl.id}`}>{bl.distributor_name}</AccountLink></div>
                      {bl.distributor_contact && <div className="text-[10px] text-[#A3A6A3]">{bl.distributor_contact}</div>}
                    </TableCell>
                    <TableCell className="text-xs text-[#575A57]">{bl.from_distributor_name || "—"}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{(bl.lines || []).length}</TableCell>
                    <TableCell className="text-right font-mono">{fmtNum(totalQty)}</TableCell>
                    <TableCell>
                      <Badge className={meta.className}>{meta.label}</Badge>
                      {bl.transfer_executed && <Badge variant="outline" className="ml-1 text-[10px]"><ClipboardCheck className="w-3 h-3 mr-1" />Transferts OK</Badge>}
                    </TableCell>
                    <TableCell className="text-right">
                      <Link to={`/bons-livraison/${bl.id}`}>
                        <Button variant="ghost" size="icon" title="Voir le détail" data-testid={`view-bl-${bl.id}`}>
                          <Eye className="w-4 h-4" />
                        </Button>
                      </Link>
                      <Button variant="ghost" size="icon" onClick={() => remove(bl)} disabled={bl.transfer_executed} data-testid={`delete-bl-${bl.id}`}>
                        <Trash2 className="w-4 h-4 text-[#C05C47]" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
